/**
 * L3: ranking, with the same rules as the Mac Quick Search panel
 * (native/quick-search/Sources/VowedKit/FuzzyMatch.swift `SearchIndex.rank`):
 * - the scorer is the CLI's own `fuzzyMatch`, imported from the one shared module;
 * - weights: title x2, tag values x1, tag keys x1, wallet x0.5, best-of;
 * - a wallet filter narrows BEFORE ranking;
 * - relevance floor: when any hit is a substring hit (>= 100), scattered-letter hits are hidden;
 * - ties break toward the shorter title, then alphabetically.
 * Phone additions: archived and deleted items never appear, and a filter with no query lists the
 * wallet alphabetically.
 */
import { fuzzyMatch } from "../../../src/search/fuzzy-match.js";
import { CATEGORIES, CATEGORY_LABEL, type CategoryId, type VaultItem, type VaultSnapshot, type Wallet } from "../contract.js";
import { filterSuggestions, parseQuery, walletCompletions, type FilterSuggestion } from "./query.js";

export interface Hit {
  item: VaultItem;
  score: number;
}

const SUBSTRING_FLOOR = 100;
const byTitle = (a: VaultItem, b: VaultItem) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

export function rankItems(query: string, items: VaultItem[], wallets: Set<string> | null = null, limit = 50): Hit[] {
  const pool = items.filter((i) => !i.archived && !i.deleted && (!wallets || wallets.has(i.wallet)));
  const q = query.trim();
  if (!q) return [...pool].sort(byTitle).slice(0, limit).map((item) => ({ item, score: 0 }));

  let hits: Hit[] = [];
  for (const item of pool) {
    const name = fuzzyMatch(q, item.title);
    const tagValues = fuzzyMatch(q, Object.values(item.tags).join(" "));
    const tagKeys = fuzzyMatch(q, Object.keys(item.tags).join(" "));
    const wallet = fuzzyMatch(q, item.wallet);
    if (!(name.match || tagValues.match || tagKeys.match || wallet.match)) continue;
    const score = Math.max(
      name.match ? name.score * 2 : 0,
      tagValues.match ? tagValues.score : 0,
      tagKeys.match ? tagKeys.score : 0,
      wallet.match ? wallet.score * 0.5 : 0,
    );
    hits.push({ item, score });
  }
  if (hits.some((h) => h.score >= SUBSTRING_FLOOR)) hits = hits.filter((h) => h.score >= SUBSTRING_FLOOR);
  hits.sort((a, b) => b.score - a.score || a.item.title.length - b.item.title.length || byTitle(a.item, b.item));
  return hits.slice(0, limit);
}

export interface WalletCount {
  wallet: Wallet;
  count: number;
}

/** One vault tag (key and value) and how many listed items carry it. */
export interface TagCount {
  key: string;
  value: string;
  count: number;
}

/** Most tags shown in the Tags group. A common fragment (a domain suffix) must not flood it. */
export const TAG_RESULT_LIMIT = 8;

export interface SearchResults {
  /** Empty field: the idle screen (suggestions, no results). */
  idle: boolean;
  items: Hit[];
  /**
   * Tags whose key or value matches the query, counted over the items in scope, most-used first
   * (01 §2.1: results include tags). Tapping one searches for its value.
   */
  tags: TagCount[];
  /** Wallets whose name matches the query. Tapping one applies `w:<id> `. */
  wallets: WalletCount[];
  /** Categories whose label matches the query. Tapping one opens that list. */
  categories: { category: CategoryId; count: number }[];
  unknownWallets: string[];
  /** Set while the `w:` token is being typed: wallets to complete, with counts. */
  completing: { fragment: string; options: WalletCount[] } | null;
  /** Set in `=` filter mode. */
  filters: FilterSuggestion[] | null;
  /** The wallets the parsed token selected (drives the scope pill). */
  selectedWallets: string[];
  query: string;
}

export function walletCounts(snapshot: VaultSnapshot): Map<string, number> {
  const counts = new Map<string, number>(snapshot.wallets.map((w) => [w.id, 0]));
  for (const i of snapshot.items) if (!i.archived && !i.deleted) counts.set(i.wallet, (counts.get(i.wallet) ?? 0) + 1);
  return counts;
}

/** Tags whose key or value contains `lq` (lower case), over listed items in scope. */
function matchingTags(lq: string, items: VaultItem[], scope: Set<string> | null): TagCount[] {
  const found = new Map<string, TagCount>();
  for (const i of items) {
    if (i.archived || i.deleted || (scope && !scope.has(i.wallet))) continue;
    for (const [key, value] of Object.entries(i.tags)) {
      if (!key.toLowerCase().includes(lq) && !value.toLowerCase().includes(lq)) continue;
      const id = JSON.stringify([key, value]);
      const tag = found.get(id) ?? { key, value, count: 0 };
      tag.count++;
      found.set(id, tag);
    }
  }
  return [...found.values()]
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key) || a.value.localeCompare(b.value))
    .slice(0, TAG_RESULT_LIMIT);
}

export function searchResults(text: string, snapshot: VaultSnapshot, limit = 50): SearchResults {
  const order = snapshot.wallets.map((w) => w.id);
  const counts = walletCounts(snapshot);
  const byId = new Map(snapshot.wallets.map((w) => [w.id, w]));
  const base: SearchResults = {
    idle: false,
    items: [],
    tags: [],
    wallets: [],
    categories: [],
    unknownWallets: [],
    completing: null,
    filters: null,
    selectedWallets: [],
    query: "",
  };

  const filters = filterSuggestions(text);
  if (filters) return { ...base, filters };

  const p = parseQuery(text, order);
  const q = p.query.trim();
  const result: SearchResults = { ...base, unknownWallets: p.unknown, selectedWallets: p.wallets, query: q };

  if (p.completing !== null) {
    result.completing = {
      fragment: p.completing,
      options: walletCompletions(p.completing, order, p.wallets).map((id) => ({ wallet: byId.get(id)!, count: counts.get(id) ?? 0 })),
    };
    return result;
  }
  // An unknown wallet is named, and nothing is listed under a filter that means nothing.
  if (p.unknown.length > 0 && p.wallets.length === 0) return result;
  if (!q && p.wallets.length === 0) return { ...result, idle: true };

  const scope = p.wallets.length > 0 ? new Set(p.wallets) : null;
  result.items = rankItems(q, snapshot.items, scope, limit);
  if (q) {
    const lq = q.toLowerCase();
    result.tags = matchingTags(lq, snapshot.items, scope);
    result.wallets = snapshot.wallets
      .filter((w) => !scope && (w.name.toLowerCase().includes(lq) || w.id.toLowerCase().includes(lq)))
      .map((w) => ({ wallet: w, count: counts.get(w.id) ?? 0 }));
    result.categories = CATEGORIES.filter((c) => CATEGORY_LABEL[c].toLowerCase().includes(lq)).map((c) => ({
      category: c,
      count: snapshot.items.filter((i) => i.category === c && !i.archived && !i.deleted && (!scope || scope.has(i.wallet))).length,
    }));
  }
  return result;
}
